import { TGetOrderResponse } from '@p2p/services';

import { getQuickPayBank, QuickPayBank } from './quickPay.banks';

/** Сколько ждём ухода страницы в фон, прежде чем считать, что приложение не открылось. */
export const QUICK_PAY_APP_OPEN_TIMEOUT_MS = 1800;
/** Android: пауза перед фоллбеком intent://, если основная схема не увела страницу в фон. */
export const QUICK_PAY_ANDROID_FALLBACK_DELAY_MS = 50;
/**
 * Таймер проверки сработал позже срока больше чем на это время — страницу замораживали
 * в фоне (iOS так делает, пока открыто приложение банка), значит переход был.
 */
export const QUICK_PAY_SUSPEND_MARGIN_MS = 1000;
/** P2P-3112: пауза между схемами в очереди переходов (ТЗ: 50 мс). */
export const QUICK_PAY_SCHEME_INTERVAL_MS = 50;
/**
 * P2P-3112: пауза перед веб-версией банка в конце очереди. Приложение открывается не мгновенно:
 * уйди страница на сайт банка через 50 мс после схемы, которая сработала, плательщик, вернувшись
 * из приложения, увидел бы сайт банка вместо реквизитов и окна «Вы оплатили?».
 */
export const QUICK_PAY_WEB_FALLBACK_DELAY_MS = 1000;

export const isAndroidUserAgent = (userAgent: string): boolean =>
  /android/.test(userAgent.toLowerCase());

/**
 * Телефон или планшет по User-Agent. Общий usePlatform считает iOS и настольный Mac
 * («macintosh»), а поведение кнопки на телефоне и на десктопе разное (P2P-3088): на
 * телефоне открывается приложение банка, на десктопе - его веб-версия в новой вкладке.
 * iPadOS Safari представляется Mac'ом - его отличаем по сенсорному экрану.
 */
export const isMobileUserAgent = (userAgent: string, maxTouchPoints = 0): boolean => {
  const ua = userAgent.toLowerCase();

  if (/android|iphone|ipad|ipod/.test(ua)) {
    return true;
  }

  return /macintosh/.test(ua) && maxTouchPoints > 1;
};

/**
 * P2P-3088: площадка для кнопки - на телефоне и планшете открываем приложение банка, на
 * десктопе его веб-версию. Тем же значением помечаются события диплинка в Sentry.
 */
export const getQuickPayPlatform = (): 'ios' | 'android' | 'desktop' => {
  const { userAgent, maxTouchPoints } = window.navigator;

  if (!isMobileUserAgent(userAgent, maxTouchPoints)) {
    return 'desktop';
  }

  return isAndroidUserAgent(userAgent) ? 'android' : 'ios';
};

type QuickPayConditions = {
  order: TGetOrderResponse;
  isFail: boolean;
  userAgent: string;
  maxTouchPoints?: number;
};

/**
 * Банк блока быстрой оплаты, если блок надо показать: тема с кнопкой и client_bank_code
 * её банка (gazprom_page + gazprom - P2P-3038, ozon_page или main_page + ozon - P2P-3039,
 * P2P-3088), реквизит, который ссылка банка поддерживает, и страница открыта не после
 * неудачной попытки (?status=fail). На десктопе - только если у ссылки есть веб-версия
 * банка (у Газпромбанка и у таджикского перевода Озона её нет). Иначе undefined -
 * страница работает как main_page, без блока.
 */
export const getAvailableQuickPayBank = ({
  order,
  isFail,
  userAgent,
  maxTouchPoints,
}: QuickPayConditions): QuickPayBank | undefined => {
  const bank = getQuickPayBank(order);

  if (!bank || isFail) {
    return undefined;
  }

  const link = bank.resolveLink(order);

  if (!link) {
    return undefined;
  }

  return isMobileUserAgent(userAgent, maxTouchPoints) || link.webUrl ? bank : undefined;
};

/**
 * Переход в приложение. Страница может быть открыта во фрейме мерчанта — уводим верхнее
 * окно; не пустили (другой домен, песочница фрейма) — текущее.
 */
export const openAppLink = (link: string) => {
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = link;

      return;
    }
  } catch {
    // Нет доступа к верхнему окну — открываем в текущем.
  }

  window.location.href = link;
};

/**
 * P2P-3088, десктоп: веб-версия банка в НОВОЙ вкладке, чтобы страница с реквизитами и
 * таймером осталась открытой. Вызывать строго внутри обработчика клика, иначе сработает
 * блокировщик попапов.
 */
export const openWebLinkInNewTab = (link: string) => {
  window.open(link, '_blank', 'noopener');
};
